import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, onBeforeMount, ref } from 'vue';
import { AxiosError } from 'axios';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { updateSubscription } from '@/api/billing';
import { CancelReason, PauseReason, SubscriptionStatusUpdate } from '@/api/typings/billing';
import { getProductsCount } from '@/api/product';
import { passwordCheck } from '@/api/user';
import { BModal } from 'bootstrap-vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SubscriptionPauseCancelDialog',
  props: {
    value: { type: Boolean },
    subscriptionId: null,
    allowPausing: { type: Boolean, default: true },
    allowCanceling: { type: Boolean, default: true },
    accountDisableMode: { type: Boolean },
    store: null,
    cardToken: null
  },
  emits: ['input', 'canceled', 'paused'],
  setup(__props: any, { emit }) {

const props = __props as { value: boolean, subscriptionId?: string, allowPausing: boolean, allowCanceling: boolean, accountDisableMode?: boolean, store: string, cardToken?: string };





const password = ref('');
const errorMessage = ref('');
const invalidPassword = ref(false);
const templatesCount = ref(0);
const currentAction: Ref<'cancel' | 'pause'> = ref('cancel');
const showPaymentFailedAlert = ref(false);

const processing = ref(false);
const pausingSubscription = ref(false);
const cancelingSubscription = ref(false);

const showReasons = ref(false);
const cancelReasons: Ref<CancelReason[]> = ref([]);
const pauseReasons: Ref<PauseReason[]> = ref([]);
const actionDetails = ref('');

const isVisible = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        if (value) clearDialog();
        emit('input', value);
    }
});

const modalTitle = computed(() => {
    if (showReasons.value) {
        return "We're sorry to see you go :(";
    } else {
        if (!props.allowCanceling) {
            return 'Pause Subscription';
        } else {
            return 'Cancel Subscription';
        }
    }
});

const modalSize = computed(() => {
    if (!props.allowCanceling || !props.allowPausing) return 'lg';
    return showReasons.value ? 'lg' : 'xl';
});

onBeforeMount(async () => {
    templatesCount.value = await getProductsCount();
});

const goToReasons = async (futureAction: 'cancel' | 'pause') => {
    try {
        currentAction.value = futureAction;
        processing.value = true;
        invalidPassword.value = false;

        const result = await passwordCheck(password.value);
        if (result.Valid) {
            // Valid password, show cancel reasons
            showReasons.value = true;
        } else {
            // Invalid password
            invalidPassword.value = true;
        }
    } finally {
        processing.value = false;
    }
};

const subscriptionSubmit = async (action: 'cancel' | 'pause' = 'cancel') => {
    if (!props.store) {
        console.warn(`No store given on SubscriptionPauseCancelDialog`);
        return;
    }

    try {
        processing.value = true;
        if (password.value && props.subscriptionId) {
            errorMessage.value = '';

            if (action === 'cancel') {
                cancelingSubscription.value = true;
            } else {
                pausingSubscription.value = true;
            }

            await updateSubscription(
                props.store,
                password.value,
                action === 'cancel' ? SubscriptionStatusUpdate.Cancelled : SubscriptionStatusUpdate.Paused,
                props.cardToken || undefined,
                action === 'cancel'
                    ? cancelReasons.value.map((r) => ({
                          Type: r,
                          Details: r === 'OTHER' ? actionDetails.value : ''
                      }))
                    : undefined,
                action === 'pause'
                    ? pauseReasons.value.map((r) => ({
                          Type: r,
                          Details: r === 'OTHER' ? actionDetails.value : ''
                      }))
                    : undefined
            );
            emit(action === 'cancel' ? 'canceled' : 'paused', props.subscriptionId);
            isVisible.value = false;
        }
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 402) {
            showPaymentFailedAlert.value = true;
        } else if (axiosError.response?.status === 400) {
            if (axiosError.response.data.Message) {
                if (axiosError.response.data.Message === 'Invalid Password') {
                    invalidPassword.value = true;
                } else {
                    errorMessage.value = 'Error updating subscription';
                }
            }
        } else {
            errorMessage.value = 'Error updating subscription';
            window.rumReportError(axiosError);
        }
    } finally {
        pausingSubscription.value = false;
        cancelingSubscription.value = false;
        processing.value = false;
    }
};

const clearDialog = () => {
    errorMessage.value = '';
    invalidPassword.value = false;
    password.value = '';
    showReasons.value = false;
    cancelReasons.value = [];
    pauseReasons.value = [];
    actionDetails.value = '';
    currentAction.value = 'cancel';
    showPaymentFailedAlert.value = false;
};

return { __sfc: true,props, emit, password, errorMessage, invalidPassword, templatesCount, currentAction, showPaymentFailedAlert, processing, pausingSubscription, cancelingSubscription, showReasons, cancelReasons, pauseReasons, actionDetails, isVisible, modalTitle, modalSize, goToReasons, subscriptionSubmit, clearDialog, LoaderBtn, ValidationProvider, ValidationObserver, BModal }
}

})